import { useTheme } from "styled-components";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { getPricePrecision } from "@shared/features/portfolio/utils";
import { toFixed } from "@shared/utils/numbers";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getPriceTextColor } from "@shared/common/utils";
import { DisplayCurrency } from "@shared/types";

interface UseLiveSymbolAvgPriceProps {
  symbol: string;
  initialPrice?: number;
  precision?: number;
  initialPriceTimestamp?: number;
  currency?: DisplayCurrency;
}

const useLiveSymbolAvgPrice = ({
  symbol,
  initialPrice,
  precision: propsPrecision,
  initialPriceTimestamp,
  currency,
}: UseLiveSymbolAvgPriceProps) => {
  const theme = useTheme();
  const selectedDisplayCurrency = useSelector(selectDisplayCurrency);
  const displayCurrency = currency ?? selectedDisplayCurrency;

  const pricesRef = useRef<number[]>(undefined);
  const socketPriceTimestampRef = useRef<number>(undefined);
  const [currentPrice, setCurrentPrice] = useState<number | undefined>(
    undefined
  );
  const [prevPrice, setPrevPrice] = useState<number>();
  const currentAvgPrice = currentPrice ?? initialPrice;
  const currentAvgPriceRef = useRef<number>(undefined);
  currentAvgPriceRef.current = currentAvgPrice;

  const shouldResetSocketPrice =
    initialPriceTimestamp &&
    socketPriceTimestampRef.current &&
    socketPriceTimestampRef.current < initialPriceTimestamp;

  const resetSocketData = () => {
    pricesRef.current = [];
    setCurrentPrice(undefined);
    setPrevPrice(undefined);
  };

  useEffect(() => {
    if (shouldResetSocketPrice) {
      resetSocketData();
    }
  }, [shouldResetSocketPrice]);

  useEffect(() => {
    resetSocketData();
  }, [symbol, displayCurrency]);

  const precision = useMemo(() => {
    if (propsPrecision !== undefined) return propsPrecision;
    else if (symbol === displayCurrency) return 0;
    return getPricePrecision(currentAvgPrice || 0, displayCurrency);
  }, [symbol, currentAvgPrice, displayCurrency, propsPrecision]);

  const priceWithPrecision = toFixed(currentAvgPrice, precision);
  const prevPriceWithPrecision = toFixed(prevPrice, precision);

  useEffect(() => {
    const interval = setInterval(() => {
      setPrevPrice(currentAvgPriceRef.current);
      const filteredPrices = pricesRef.current?.filter(
        (price) => price && price > 0
      );
      const numExchanges = filteredPrices?.length;
      const sumPrices = (filteredPrices as number[]).reduce(
        (sum, price) => sum + price,
        0
      );
      setCurrentPrice(
        numExchanges && sumPrices > 0 ? sumPrices / numExchanges : initialPrice
      );
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const setPrice = (price: number, index: number) => {
    if (pricesRef.current) {
      pricesRef.current[index] = price;
      socketPriceTimestampRef.current = Date.now();
    }
  };

  const color = getPriceTextColor(
    prevPriceWithPrecision,
    priceWithPrecision,
    theme
  );

  return {
    currentAvgPrice,
    setPrice,
    precision,
    color,
  };
};

export default useLiveSymbolAvgPrice;
